import { useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/store/auth.store';
import { modal } from '@/app/store/modal.store';
import { useUiStore } from '@/app/store/ui.store';
import { useLogout } from '@/shared/hooks/use-logout';
import { useNotifications } from '@/shared/hooks/use-notifications';
import { useNotificationTime } from '@/shared/hooks/use-notification-time';
import type { NotificationType } from '@/api/adapters/notifications';
import { medicalTopTabs } from '@/pages/medical/MedicalNavigation';
import { ShcLogo } from "@/components/brand/ShcLogo";

// GNB dropdown type badge (PRD 08 §2.3 / prototype app.js — colored [label] prefix on the title row).
const NOTIF_TYPE_COLOR: Record<NotificationType, string> = {
  RISK: '#dc2626',
  HR_ABNORMAL: '#dc2626',
  PRO_DELAY: '#d97706',
  SYSTEM: '#6b7280',
};
const NOTIF_TYPE_LABEL_KEY: Record<NotificationType, string> = {
  RISK: 'notifications.type.risk',
  HR_ABNORMAL: 'notifications.type.hrAbnormal',
  PRO_DELAY: 'notifications.type.proDelay',
  SYSTEM: 'notifications.type.system',
};

export function Gnb() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { t: tType } = useTranslation('medical');
  const email = useAuthStore((s) => s.email);
  const performLogout = useLogout();
  const isUserMenuOpen = useUiStore((s) => s.isUserMenuOpen);
  const setUserMenuOpen = useUiStore((s) => s.setUserMenuOpen);
  const isNotifMenuOpen = useUiStore((s) => s.isNotificationMenuOpen);
  const setNotifMenuOpen = useUiStore((s) => s.setNotificationMenuOpen);

  const userMenuBtnRef = useRef<HTMLButtonElement>(null);
  const userMenuDropRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const { notifications, unreadCount } = useNotifications();
  const formatNotificationTime = useNotificationTime();

  // PRD 08 §2.3 — dropdown shows the most recent 5 notices; badge caps at 99+.
  const recentNotifications = notifications.slice(0, 5);
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const state = useUiStore.getState();
      if (state.isUserMenuOpen) {
        const insideUserMenu =
          userMenuBtnRef.current?.contains(target) ||
          userMenuDropRef.current?.contains(target);
        if (!insideUserMenu) setUserMenuOpen(false);
      }
      if (state.isNotificationMenuOpen) {
        if (!notifRef.current?.contains(target)) setNotifMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setUserMenuOpen, setNotifMenuOpen]);

  const handleLogout = () => {
    // PRD 09 §3.5 — confirm popup → server-side termination → local logout + redirect.
    // On a server error (not 401) `useLogout` keeps the local session intact and
    // rethrows; we surface the PRD §8.2 popup and stay on the current page so the
    // user can retry (SHCT-318). Only a successful logout redirects to /login.
    setUserMenuOpen(false);
    modal.confirm(t('logout.confirmTitle'), t('logout.confirmBody'), () => {
      void (async () => {
        try {
          await performLogout();
          navigate('/login');
        } catch {
          modal.alert(t('logout.failureTitle'), t('logout.failureBody'));
        }
      })();
    });
  };

  return (
    <header className="gnb gnb--v3 gnb--dark">
      <div className="gnb__left">
        <Link to="/home" className="gnb__logo">
          <div className="gnb__logo-icon" aria-hidden="true">
            <ShcLogo size={18} />
          </div>
          {t('gnb.brand')}
        </Link>

        <nav className="gnb__tabs" aria-label={t('gnb.mainMenuAria')}>
          {medicalTopTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                className={({ isActive }) =>
                  `gnb__tab ${isActive ? 'gnb__tab--active' : ''}`
                }
              >
                <Icon size={24} />
                {t(tab.labelKey)}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="gnb__right">
        <button
          ref={userMenuBtnRef}
          type="button"
          className="gnb__user-menu"
          data-open={isUserMenuOpen ? 'true' : 'false'}
          onClick={() => setUserMenuOpen(!isUserMenuOpen)}
        >
          <span>{email}</span>
        </button>
        <div
          ref={userMenuDropRef}
          className="gnb__dropdown"
          data-visible={isUserMenuOpen ? 'true' : 'false'}
        >
          <Link
            to="/my-profile"
            className="gnb__dropdown-item"
            onClick={() => setUserMenuOpen(false)}
          >
            {t('gnb.myProfile')}
          </Link>
          <Link
            to="/settings"
            className="gnb__dropdown-item"
            onClick={() => setUserMenuOpen(false)}
          >
            {t('gnb.termsPolicies')}
          </Link>
          <div className="gnb__dropdown-divider" />
          <button
            type="button"
            className="gnb__dropdown-item"
            onClick={handleLogout}
          >
            {t('actions.logout')}
          </button>
        </div>

        <div className="gnb__notif-wrap" ref={notifRef}>
          <button
            type="button"
            className="gnb__notif-btn"
            title={t('notifications.label')}
            aria-label={t('notifications.label')}
            onClick={() => setNotifMenuOpen(!isNotifMenuOpen)}
          >
            <Bell size={24} />
            {unreadCount > 0 && (
              <span className="gnb__notif-badge">{badgeLabel}</span>
            )}
          </button>
          <div
            className="gnb__notif-dropdown"
            data-visible={isNotifMenuOpen ? 'true' : 'false'}
          >
            <div className="gnb__notif-dropdown-head">
              <span>{t('notifications.title')}</span>
              <Link
                to="/notifications"
                className="link-subtle"
                onClick={() => setNotifMenuOpen(false)}
              >
                {t('notifications.viewAll')}
              </Link>
            </div>
            {recentNotifications.length === 0 ? (
              <div className="gnb__notif-empty">{t('notifications.empty')}</div>
            ) : (
              <ul className="gnb__notif-list">
                {recentNotifications.map((n) => (
                  <li
                    key={n.id}
                    className={`gnb__notif-item ${n.status === 'UNREAD' ? 'gnb__notif-item--unread' : ''}`}
                    onClick={() => {
                      setNotifMenuOpen(false);
                      navigate('/notifications');
                    }}
                  >
                    <div className="gnb__notif-item-top">
                      <span className="gnb__notif-item-time">
                        {formatNotificationTime(n.createdAt)}
                      </span>
                    </div>
                    <div className="gnb__notif-item-title">
                      <span
                        className="gnb__notif-item-badge"
                        style={{ color: NOTIF_TYPE_COLOR[n.type] }}
                      >
                        [{tType(NOTIF_TYPE_LABEL_KEY[n.type])}]
                      </span>{' '}
                      {n.title}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
